import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronDown,
  Compass,
  Database,
  Folders,
  IdCard,
  MoreVertical,
  Search,
  SquareArrowRight,
} from "lucide-react";
import useRequest from "../../../components/hook/use-req";
import Pagination from "../../../components/pagination";

// Reseller home (Figma "Partner Console").
//  - GET /resellers/console/summary?date_from&date_to — the six cards
//  - GET /resellers/clients?page&limit&search — "Your Clients"
// A metric with `value: null` (data_source "not_configured") shows "—".
// There's no partner-profile API yet, so the partner card reads the signed-in
// user's details saved at login (userDetails), and "Configure billing and
// packages" can't be ticked off until billing exists.

interface MetricValue {
  value: number | null;
  new_in_period?: number;
  data_source?: string;
}

interface ConsoleSummary {
  total_clients?: MetricValue;
  total_profiles?: MetricValue;
  revenue?: MetricValue & { currency?: string };
  events_processed?: MetricValue;
  connected_sources?: MetricValue;
  active_destinations?: MetricValue;
}

interface ResellerClient {
  id: string;
  name: string;
  admin_email?: string;
  admin_phone?: string;
  phone?: string;
  current_plan: string | null;
  added_on: string;
  is_active?: boolean;
  invite_sent?: boolean;
}

const RANGE_PRESETS = [
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
];

const fmtDay = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "long" });

// The last `days` days, ending today: start of the first day to end of today.
const rangeDates = (days: number) => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);
  return { start, end };
};
const rangeLabel = (days: number) => {
  const { start, end } = rangeDates(days);
  return `${fmtDay(start)} - ${fmtDay(end)} ${end.getFullYear()}`;
};

const compact = (n: number) => new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
const money = (n: number, currency: string) => new Intl.NumberFormat("en", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);

// "24 Aug, 2026 10.10 AM"
const fmtAddedOn = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).replace(/ (\d{4})$/, ", $1");
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(":", ".");
  return `${date} ${time}`;
};

const readUser = (): Record<string, any> => {
  try {
    return JSON.parse(localStorage.getItem("userDetails") || "{}") || {};
  } catch {
    return {};
  }
};

const firstOf = (obj: Record<string, any>, keys: string[]) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

const card = "rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900";

interface StatCardProps {
  icon: React.ElementType;
  title: string;
  value: string;
  description: string;
  note?: string;
  linkLabel?: string;
  onLink?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, title, value, description, note, linkLabel, onLink }) => (
  <div className={`flex flex-col p-4 ${card}`}>
    <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
      <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
      {title}
    </p>
    <p className="mt-3 text-xl font-medium text-gray-900 dark:text-gray-100">
      {value}
      {note && <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">{note}</span>}
    </p>
    <p className="mt-3 text-xs text-gray-600 dark:text-gray-400 leading-relaxed flex-1">{description}</p>
    {linkLabel && (
      <button
        type="button"
        onClick={onLink}
        className="mt-3 inline-flex items-center gap-1.5 self-start text-sm font-medium text-indigo-700 dark:text-indigo-400 underline underline-offset-2 hover:text-indigo-800"
      >
        {linkLabel}
        <ArrowRight className="w-4 h-4" />
      </button>
    )}
  </div>
);

const PartnerConsole: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState(RANGE_PRESETS[0]);
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const token = localStorage.getItem("authToken");
  const { makeRequest: getSummary, loading: summaryLoading } = useRequest("/resellers/console/summary", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: getClients, loading: clientsLoading } = useRequest("/resellers/clients", "GET", { Authorization: `Bearer ${token}` });
  const [summary, setSummary] = useState<ConsoleSummary | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [clients, setClients] = useState<ResellerClient[]>([]);
  const [clientsError, setClientsError] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Drops a slower, older response that lands after a newer one.
  const clientsReqRef = useRef(0);
  const summaryReqRef = useRef(0);

  const user = React.useMemo(readUser, []);
  const partner = {
    name: firstOf(user, ["org_name", "organization_name", "company_name", "business_name", "reseller_name", "name", "full_name"]) || "Partner",
    logo: firstOf(user, ["org_logo", "logo", "logo_url", "avatar", "profile_picture"]),
    phone: firstOf(user, ["phone", "phone_number", "mobile", "mobile_number"]),
    website: firstOf(user, ["website", "website_url", "company_website"]),
    industry: firstOf(user, ["industry", "org_industry"]),
    joined: firstOf(user, ["created_at", "date_joined", "joined_at"]),
  };
  const joinedLabel = partner.joined && !Number.isNaN(new Date(partner.joined).getTime())
    ? new Date(partner.joined).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : "";

  useEffect(() => {
    if (!rangeOpen) return;
    const close = (e: MouseEvent) => {
      if (rangeRef.current && !rangeRef.current.contains(e.target as Node)) setRangeOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [rangeOpen]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    const reqId = ++summaryReqRef.current;
    const { start, end } = rangeDates(range.days);
    getSummary(undefined, { date_from: start.toISOString(), date_to: end.toISOString() }).then(([res, status]) => {
      if (reqId !== summaryReqRef.current) return;
      const ok = status >= 200 && status < 300 && res?.status !== false;
      setSummary(ok ? res : null);
      setSummaryError(!ok);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.days]);

  useEffect(() => {
    const reqId = ++clientsReqRef.current;
    getClients(undefined, { page, limit: pageSize, ...(debouncedSearch ? { search: debouncedSearch } : {}) }).then(([res, status]) => {
      if (reqId !== clientsReqRef.current) return;
      const ok = status >= 200 && status < 300 && Array.isArray(res?.clients);
      setClientsError(!ok);
      setClients(ok ? res.clients : []);
      setTotalPages(ok ? res.total_pages || 1 : 1);
      setTotalClients(ok ? res.total ?? res.clients.length : 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch]);

  const scrollToClients = () => tableRef.current?.scrollIntoView({ behavior: "smooth" });

  const metric = (m?: MetricValue, fmt: (n: number) => string = (n) => n.toLocaleString()) => {
    if (summaryLoading && !summary) return "…";
    return m?.value === null || m?.value === undefined ? "—" : fmt(m.value);
  };
  const notSetUp = (m?: MetricValue) => (m && m.value === null && m.data_source === "not_configured" ? "Not set up yet" : undefined);
  const newClients = summary?.total_clients?.new_in_period;

  // Setup checklist. Profile = the partner card has its details; first client
  // = any client on the account; billing has no API yet, so it stays open.
  const hasAnyClient = (summary?.total_clients?.value ?? 0) > 0 || (!debouncedSearch && totalClients > 0);
  const setupSteps = [
    { label: "Complete Partner Profile", done: !!(partner.phone && (partner.website || partner.industry)) },
    { label: "Add your first ConvoHub client", done: hasAnyClient, onClick: hasAnyClient ? undefined : () => navigate("/reseller/clients/new") },
    { label: "Configure billing and packages", done: false },
  ];
  const setupDone = setupSteps.filter((s) => s.done).length;

  const cards: StatCardProps[] = [
    {
      icon: Building2,
      title: "Total Clients",
      value: metric(summary?.total_clients),
      note: newClients ? `+${newClients} new in this period` : undefined,
      description: "Active customer workspaces currently managed through your reseller account.",
      linkLabel: "View Clients",
      onLink: scrollToClients,
    },
    {
      icon: IdCard,
      title: "Total Profiles",
      value: metric(summary?.total_profiles, compact),
      note: notSetUp(summary?.total_profiles),
      description: "Customer profiles available across all active client workspaces.",
      linkLabel: "View Profiles",
    },
    {
      icon: ChartColumn,
      title: "Total Revenue",
      value: metric(summary?.revenue, (n) => money(n, summary?.revenue?.currency || "USD")),
      note: notSetUp(summary?.revenue),
      description: "Revenue generated from active client plans and recurring subscriptions.",
    },
    {
      icon: Database,
      title: "Events Processed",
      value: metric(summary?.events_processed, compact),
      note: notSetUp(summary?.events_processed),
      description: "Customer, campaign, conversation, and behavioural events processed across clients.",
    },
    {
      icon: Folders,
      title: "Connected Sources",
      value: metric(summary?.connected_sources),
      note: notSetUp(summary?.connected_sources),
      description: "Data sources currently connected across all client workspaces.",
      linkLabel: "View Sources",
    },
    {
      icon: SquareArrowRight,
      title: "Active Destinations",
      value: metric(summary?.active_destinations),
      note: notSetUp(summary?.active_destinations),
      description: "Destinations currently receiving synced audiences, profiles, or customer data.",
      linkLabel: "View Destinations",
    },
  ];

  const COLUMNS = ["Company Name", "Current Bundle", "Added On", "Email", "Phone", "Actions"];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 min-h-full">
      <h1 className="text-2xl font-medium text-gray-900 dark:text-gray-100">Partner Console</h1>
      <p className="text-sm text-gray-700 dark:text-gray-400">Setup and monitor client activity</p>

      {/* Partner card + setup checklist */}
      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-[2fr_3fr]">
        <div className={`p-4 ${card}`}>
          <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            {partner.logo ? (
              <img src={partner.logo} alt="" className="w-6 h-6 rounded-full object-cover" />
            ) : (
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300">
                {partner.name.charAt(0).toUpperCase()}
              </span>
            )}
            {partner.name}
          </p>
          <dl className="mt-3 divide-y divide-gray-100 dark:divide-gray-800 text-sm text-gray-700 dark:text-gray-300">
            <div className="py-2.5">{partner.phone || <span className="text-gray-400">No phone number</span>}</div>
            <div className="py-2.5">
              {partner.website ? (
                <a
                  href={/^https?:\/\//i.test(partner.website) ? partner.website : `https://${partner.website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-700 dark:text-indigo-400 underline underline-offset-2 break-all"
                >
                  {partner.website}
                </a>
              ) : (
                <span className="text-gray-400">No website</span>
              )}
            </div>
            <div className="py-2.5">{partner.industry || <span className="text-gray-400">No industry</span>}</div>
            <div className="py-2.5">Joined: {joinedLabel || "—"}</div>
          </dl>
        </div>

        <div className={`p-4 ${card}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <Compass className="w-4 h-4" />
              Setup
            </p>
            <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300">
              {setupDone} out of {setupSteps.length} complete
            </span>
          </div>
          <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-800">
            {setupSteps.map((s) => (
              <li key={s.label}>
                <button
                  type="button"
                  disabled={!s.onClick}
                  onClick={s.onClick}
                  className="w-full flex items-center gap-3 py-3 text-left text-sm text-gray-800 dark:text-gray-200 disabled:cursor-default enabled:hover:text-indigo-700"
                >
                  <span
                    className={`flex items-center justify-center w-6 h-6 rounded-full shrink-0 ${
                      s.done ? "bg-emerald-400 text-white ring-2 ring-emerald-100 dark:ring-emerald-500/20" : "bg-indigo-50 dark:bg-gray-800 text-indigo-200 dark:text-gray-600"
                    }`}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Setup Clients</h2>
          <p className="max-w-sm text-sm text-gray-700 dark:text-gray-400">
            Manage client workspaces, monitor usage, and track overall reseller performance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative" ref={rangeRef}>
            <button
              type="button"
              onClick={() => setRangeOpen((o) => !o)}
              className="flex items-center gap-2 h-11 px-3 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-200"
            >
              <CalendarDays className="w-4 h-4" />
              {rangeLabel(range.days)}
              <ChevronDown className={`w-4 h-4 ml-3 transition-transform ${rangeOpen ? "rotate-180" : ""}`} />
            </button>
            {rangeOpen && (
              <div className="absolute right-0 z-20 mt-1 w-full min-w-[180px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1">
                {RANGE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setRange(p);
                      setRangeOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      p.id === range.id ? "font-semibold text-indigo-700 dark:text-indigo-400" : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate("/reseller/clients/new")}
            className="h-11 px-3 rounded-md bg-indigo-700 hover:bg-indigo-800 text-sm font-medium text-white transition-colors"
          >
            Add New Client
          </button>
        </div>
      </div>

      {summaryError && <p className="mt-6 text-sm text-red-600 dark:text-red-400">Couldn't load the summary for this period. Try again shortly.</p>}
      <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <StatCard key={c.title} {...c} />
        ))}
      </div>

      <div ref={tableRef} className="mt-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Your Clients
            {totalClients > 0 && <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">({totalClients.toLocaleString()})</span>}
          </h3>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
            />
          </div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="text-left text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800">
              <tr>
                {COLUMNS.map((h) => (
                  <th key={h} className={`px-4 py-4 font-medium ${h === "Actions" ? "text-center" : ""}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {clientsLoading && clients.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-16 text-center text-gray-500 dark:text-gray-400">
                    Loading clients…
                  </td>
                </tr>
              ) : clientsError ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-16 text-center text-red-600 dark:text-red-400">
                    Couldn't load your clients. Try again shortly.
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-16 text-center text-gray-500 dark:text-gray-400">
                    {debouncedSearch ? `No clients match "${debouncedSearch}".` : "No clients yet — add your first client to get started."}
                  </td>
                </tr>
              ) : (
                clients.map((c) => (
                  <tr key={c.id} className="text-gray-900 dark:text-gray-200">
                    <td className="px-4 py-5">
                      <span className="flex items-center gap-2.5">
                        <Building2 className="w-5 h-5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                        <span>{c.name}</span>
                        {c.is_active === false && (
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[11px] font-medium text-gray-500">Inactive</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-5 font-medium">{c.current_plan || <span className="font-normal text-gray-400">—</span>}</td>
                    <td className="px-4 py-5 font-medium whitespace-nowrap">{c.added_on ? fmtAddedOn(c.added_on) : "—"}</td>
                    <td className="px-4 py-5 font-medium">{c.admin_email || <span className="font-normal text-gray-400">—</span>}</td>
                    <td className="px-4 py-5 font-medium whitespace-nowrap">
                      {c.admin_phone || c.phone || <span className="font-normal text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-5 text-center">
                      <button type="button" title="Actions" className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-indigo-600 dark:text-indigo-400">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalClients > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            isLoading={clientsLoading}
          />
        )}
      </div>
    </div>
  );
};

export default PartnerConsole;
